import { FormEvent, ReactNode, useId } from 'react';
import { Dialog } from 'primereact/dialog';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useRestoreFocus } from '@/hooks/useRestoreFocus';
import { Button } from './button';
import { formDialogLayout } from './form-dialog-layout';

/**
 * FormDialog — standard form-in-dialog primitive (T-110).
 *
 * Why this exists:
 * - Every page used to compose a PrimeReact `<Dialog>` + ad-hoc footer + form,
 *   which drifted (button order, RTL handling, isPending wiring, focus rings).
 * - Single source for: header, sticky footer with primary/secondary, disabled-while-pending
 *   (UB-002), `onSubmit` Enter-key handling, and RTL flip.
 *
 * Notes:
 * - Footer is sticky (PrimeReact `footer` slot stays below the body).
 * - Submit button is auto-disabled when `isPending === true` (UB-002).
 * - Wrap the body in a `<form>` so Enter submits; the wrapper does this for you.
 */
/**
 * POS-I350: an open dropdown, multi-select or autocomplete list. Escape then closes that list only (the
 * component handles it itself); the dialog and what was typed in it stay.
 */
const OPEN_LIST_PANEL = ".p-dropdown-panel, .p-multiselect-panel, .p-autocomplete-panel, .p-cascadeselect-panel";

export interface FormDialogProps {
  open: boolean;
  title: string;
  subtitle?: string;
  /** Width (e.g. '32rem', '90vw'). Defaults to '34rem'. */
  width?: string;
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  /** Set when the underlying mutation is in-flight to disable the submit button (UB-002). */
  isPending?: boolean;
  /** Disable submit while the form is invalid (greys the button, in addition to native `required`). */
  submitDisabled?: boolean;
  /** Variant of the submit button. `default` renders the canonical primary (gradient). */
  variant?: 'default' | 'destructive';
  /** Hide the footer if the caller renders its own (rare). */
  hideFooter?: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
  /** Extra footer content rendered at the START (inline-start) of the primary actions — left in LTR, right in RTL. */
  footerLeading?: ReactNode;
  className?: string;
  /** Show the PrimeReact maximize toggle in the header (wide/long forms). Default off. */
  maximizable?: boolean;
  /** Optional icon rendered before the title in the header. */
  icon?: ReactNode;
  /**
   * POS-I266: 'full' = near full screen (96vw x 94vh): header and footer stay pinned, the body scrolls.
   * Default keeps the width-based sizing every other dialog uses.
   */
  size?: 'default' | 'large' | 'full';
  /** Extra header content next to the title (status chips, plan name). */
  headerExtra?: ReactNode;
  /** Footer shows only the cancel/close button (no submit), e.g. on a read-only tab. */
  hideSubmit?: boolean;
  /** POS-I333: skip the browser's native validation bubbles so the form can show its own inline messages. */
  noValidate?: boolean;
}

export function FormDialog({
  open,
  title,
  subtitle,
  width,
  children,
  submitLabel,
  cancelLabel,
  isPending = false,
  submitDisabled = false,
  variant = 'default',
  hideFooter = false,
  onSubmit,
  onClose,
  footerLeading,
  className,
  maximizable = false,
  icon,
  size = 'default',
  headerExtra,
  hideSubmit = false,
  noValidate = false,
}: FormDialogProps) {
  const { t } = useTranslation('common');
  const resolvedSubmit = submitLabel ?? t('save');
  const resolvedCancel = cancelLabel ?? t('cancel');
  // Unique per instance so stacked/nested FormDialogs never collide on `form=` (T-202 Slice 2).
  const formId = useId();
  const layout = formDialogLayout(size, width);
  // POS-I266: Esc / Cancel / X hand focus back to the control that opened the dialog.
  useRestoreFocus(open);

  const titleBlock = (
    <div>
      <p className="font-semibold text-foreground">{title}</p>
      {subtitle && (
        <p className="mt-0.5 text-xs font-normal text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );
  const header = headerExtra ? (
    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
      {icon && <span className="shrink-0" aria-hidden>{icon}</span>}
      {titleBlock}
      {headerExtra}
    </div>
  ) : icon ? (
    <div className="flex items-center gap-2">
      <span className="shrink-0" aria-hidden>{icon}</span>
      {titleBlock}
    </div>
  ) : (
    titleBlock
  );

  const footer = hideFooter ? undefined : (
    <div className="flex items-center justify-between gap-3 pt-1">
      <div className="flex-1 text-xs text-muted-foreground">{footerLeading}</div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} disabled={isPending} type="button">
          {resolvedCancel}
        </Button>
        {!hideSubmit && <Button
          type="submit"
          form={formId}
          variant={variant === 'destructive' ? 'destructive' : 'gradient'}
          disabled={isPending || submitDisabled}
          aria-busy={isPending}
        >
          {isPending ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('processing')}
            </span>
          ) : (
            resolvedSubmit
          )}
        </Button>}
      </div>
    </div>
  );

  return (
    <Dialog
      visible={open}
      onHide={onClose}
      header={header}
      footer={footer}
      style={layout.style}
      breakpoints={layout.breakpoints}
      closable={!isPending}
      draggable={false}
      resizable={false}
      maximizable={maximizable}
      className={cn(layout.sizeClassName, className)}
    >
      <form
        id={formId}
        onSubmit={onSubmit}
        noValidate={noValidate}
        className="space-y-4"
        onKeyDown={(e) => {
          if (e.key === "Escape" && document.querySelector(OPEN_LIST_PANEL)) e.stopPropagation();
        }}
      >
        {children}
      </form>
    </Dialog>
  );
}
